import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { Separator } from '../../ui/separator';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../ui/breadcrumb';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { AspectRatio } from '../../ui/aspect-ratio';
import {
  Copy,
  Package,
  MapPin,
  Phone,
  StickyNote,
  Image as ImageIcon,
  Signature,
  MessageSquare,
  AlertCircle,
  ExternalLink,
  Mail,
  Code2,
  Euro,
  X,
  EllipsisVertical,
  Edit,
  File,
  Clock,
  CheckCircle,
  XCircle,
  View,
} from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';
import copy from 'copy-to-clipboard';
import { AppDispatch, RootState } from '@/redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { DAY_FORMAT, ORDER_STATUSES, ROLES } from '../../../constants';
import usePageView from '../../../hooks/usePageView';
import { clearState } from '../../../modules/orders/store/slice';
import { doCancelOrder, doGetOrder, doUpdateOrder } from '../../../modules/orders/store/thunk';
import { doGetIssue, doGetIssuesForOrder } from '../../../modules/issues/stores/thunk';
import withPage from '../withPage';
import moment from 'moment';
import Icon from '../../Markers/icons/pickup';
import { imageMaker } from '../../../utils/common';
import DefaultMapbox from '../../Mapbox/DefaultMapbox';
import { getStatusBadgeStyles } from '../../../utils/statusStyles';
import OrderTimeLine, { formatOrderStatus } from '../../Elements/OrderTimeLine';
import { selectIssueById } from '../../../modules/issues/stores/slice';
import NewReportIssueForm from '../../Forms/Issues/NewReportIssueForm';
import OrderIssues from '../../Elements/OrderIssuesPopover';
import IssueChat from '../Issues/IssueChat';
import { getLabel } from '../../../modules/orders/api';
import OrderDetailSkeleton from './OrderDetailSkeleton';
import { TraceLogs } from '../../../components/Modals/TraceLogs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

const OrderDetailPage = ({ id, params }) => {
  const { toast } = useToast();
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: any) => state.auth.user);
  const userRole = useSelector((state: RootState) => state.auth.user.role);
  const isSuperAdmin = useSelector((state: RootState) => state.auth.user.isAdmin);
  const isAdmin = [ROLES.ADMIN].includes(userRole);
  const isAdminOrMerchant = [ROLES.ADMIN, ROLES.COMPANY_OWNER, ROLES.LOCATION_USER].includes(userRole);
  const order = useSelector((state: any) => state.order.orderDetail);
  const [markers, setMarkers] = useState([]);
  const [_isReportIssueModalOpen, setIsReportIssueModalOpen] = useState(false);
  const [selectedIssueId, setSelectedIssueId] = useState(null);
  const issue = useSelector((state) => selectIssueById(state, selectedIssueId));
  const [isIssueLoaded, setIsIssueLoaded] = useState(false);
  const [isIssueModalOpen, setIsIssueModalOpen] = useState(false);
  const [showLogsForOrder, setShowLogsForOrder] = useState(null);
  const [isTraceLogModalOpen, setIsTraceLogModalOpen] = useState(false);
  const [editFormParams, setEditFormParams] = useState({ status: order?.status });
  const history = useHistory();

  // Loading states
  const [isPrintingLabels, setIsPrintingLabels] = useState(false);
  const [isCancellingOrder, setIsCancellingOrder] = useState(false);

  // Emit page view event
  usePageView(window.location.href);

  useEffect(() => {
    if (params?.issueId) {
      setSelectedIssueId(params.issueId);
      if (params.issueId) {
        console.log('Fetching issue', params.issueId);
        dispatch(doGetIssue({ id: params.issueId })).then(() => {
          setIsIssueLoaded(true);
        });
      }
    }
  }, [params]);

  useEffect(() => {
    if (isIssueLoaded && issue) {
      setIsIssueModalOpen(true); // Only open modal after issue is loaded
    }
  }, [isIssueLoaded, issue]);

  useEffect(() => {
    dispatch(clearState()); // Clear the state first, no no older order data is shown...
    dispatch(doGetOrder({ id }));
  }, []);

  useEffect(() => {
    if (order) {
      setEditFormParams({ status: order?.status });
      dispatch(doGetIssuesForOrder({ orderId: order?.id, status: 'active' }));
      dispatch(doGetIssuesForOrder({ orderId: order?.id, status: 'resolved' }));
    }
  }, [order, userRole]);

  const onClickCopy = (text: string) => {
    copy(text);
    toast({
      title: 'Copied!',
      description: `${text}`,
    });
  };

  // State for popovers/dialogs
  const [isActionsPopoverOpen, setIsActionsPopoverOpen] = useState(false);
  const [isIssuesPopoverOpen, setIsIssuesPopoverOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isReportIssueDialogOpen, setIsReportIssueDialogOpen] = useState(false);

  const SignatureDisplay = ({ signature }) => {
    if (signature) {
      let src = signature;
      if (!signature.startsWith('data:image')) {
        src = `data:image/png;base64,${signature}`;
      }
      return (
        <>
          <img
            id="sig"
            src={src}
            alt="Signature"
            style={{
              maxWidth: '100%',
              background: '#F4F4F4',
              height: '100%',
              width: '100%',
            }}
          />
        </>
      );
    } else {
      return;
    }
  };

  useEffect(() => {
    if (order && order.pickupAddressObject && order.deliveryAddressLat) {
      const newMarkers = [
        {
          id: 1,
          content: <img src={imageMaker(Icon())} />,
          latitude: Number(order.pickupAddressObject?.coordinates?.lat),
          longitude: Number(order.pickupAddressObject?.coordinates?.lng),
          offsetX: 0,
          offsetY: 0,
          className: 'order-marker',
        },
        {
          id: 2,
          content: <img src="/icons/dropoff.svg" />,
          latitude: order.deliveryAddressObject?.coordinates?.lat
            ? Number(order.deliveryAddressObject.coordinates.lat)
            : Number(order?.deliveryAddressLat),
          longitude: order.deliveryAddressObject?.coordinates?.lng
            ? Number(order.deliveryAddressObject.coordinates.lng)
            : Number(order?.deliveryAddressLng),
          offsetX: 17,
          offsetY: -10,
          className: 'order-marker',
        },
      ];
      setMarkers(newMarkers);
    }
  }, [order]);

  const handleGetLabel = (orderId) => {
    (async () => {
      setIsPrintingLabels(true);
      try {
        const response = await getLabel({ id: orderId });
        dispatch(doGetOrder({ id }));
        const pdfData = response.response.label;
        const pdfBlob = await (await fetch(`data:application/pdf;base64,${pdfData}`)).blob();
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl, '_blank');
      } catch (error) {
        console.error('Error while opening label PDF:', error);
        alert('An error occurred while trying to open the label PDF. Please try again later or contact support.');
        setIsPrintingLabels(false);
      }
      setIsPrintingLabels(false);
    })();
  };

  const handleCancelOrder = (orderId) => {
    (async () => {
      setIsCancellingOrder(true);
      try {
        await dispatch(
          doCancelOrder({
            orderId: orderId,
          }),
        );
        await dispatch(doGetOrder({ id }));
      } catch (error) {
        console.error('Error cancelling order:', error);
        toast({
          title: 'Error',
          description: 'Failed to cancel order. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setIsCancellingOrder(false);
      }
    })();
  };

  const handleUpdateOrder = (orderId: string, params: any) => {
    // orderId = db id
    (async () => {
      try {
        await dispatch(doUpdateOrder({ id: orderId, ...params }));
        await dispatch(doGetOrder({ id }));
      } catch (error) {
        console.error('Error updating order:', error);
        toast({
          title: 'Error',
          description: 'Failed to update order. Please try again.',
          variant: 'destructive',
        });
      }
    })();
  };

  const tntUrl = useMemo(() => {
    const h = order?.hash ? `?hash=${order.hash}` : '';
    if (order?.createdBy === 'Ricoh') {
      // send barcode tnt url for ricoh only
      return `${process.env.REACT_APP_TRACKING_URL}barcode/${order.barcode}${h}`;
    }
    return process.env.REACT_APP_TRACKING_URL + order?.orderId + h;
  }, [order]);

  const carrierTntUrl = useMemo(
    () => order?.externalIds.find((e) => e.externalType === 'CARRIER_TRACK_TRACE_URL')?.externalId,
    [order],
  );

  if (!order) {
    return <OrderDetailSkeleton />;
  }

  return (
    <div className="bg-white">
      <div className="flex justify-center w-full">
        <TooltipProvider>
          <div className="space-y-6 w-full">
            {/* Header: Breadcrumbs and Actions */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink href="/orders" className="text-xl font-bold text-black">
                      Orders
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage className="text-m font-bold text-black-600">{order.orderId}</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>

              <div className="flex items-center gap-2 flex-wrap">
                {order.orderBundle?.orders?.length > 1 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Package className="h-4 w-4 mr-2" />
                        Bundled Orders
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      {order.orderBundle?.orders?.map((bundledOrder) => (
                        <DropdownMenuItem
                          key={bundledOrder.id}
                          onClick={() => {
                            if (bundledOrder.orderId !== order.orderId) {
                              window.location.href = `/orders/${bundledOrder.orderId}`;
                            }
                          }}
                          className={bundledOrder.orderId === order.orderId ? 'bg-muted' : ''}
                        >
                          {bundledOrder.orderId}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {order.claim && (
                  <Button variant="outline" size="sm" onClick={() => history.push(`/claims/${order.claim.id}`)}>
                    {order.claim.status === 'FILED' && (
                      <>
                        {' '}
                        <Clock className="h-4 w-4 mr-2 text-blue-500" /> Claim Filed
                      </>
                    )}
                    {order.claim.status === 'GRANTED' && (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2 text-green-500" /> Claim Granted
                      </>
                    )}
                    {order.claim.status === 'UNDER_REVIEW' && (
                      <>
                        <View className="h-4 w-4 mr-2 text-green-500" /> Claim Under Review
                      </>
                    )}
                    {order.claim.status === 'REJECTED' && (
                      <>
                        <XCircle className="h-4 w-4 mr-2 text-red-500" /> Claim Rejected{' '}
                      </>
                    )}
                  </Button>
                )}

                {/* Issues Popover */}
                <Popover open={isIssuesPopoverOpen} onOpenChange={setIsIssuesPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm">
                      <AlertCircle className="h-4 w-4 mr-2" />
                      Issues
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-200" align="end">
                    <OrderIssues orderId={order?.id} setPopoverIssuesVisible={() => setIsIssuesPopoverOpen(false)} />
                    <Button
                      size="sm"
                      className="w-full mt-2"
                      onClick={() => {
                        setIsReportIssueDialogOpen(true);
                        setIsIssuesPopoverOpen(false);
                      }}
                    >
                      Report New Issue
                    </Button>
                  </PopoverContent>
                </Popover>

                {/* {isAdminOrMerchant && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => setIsEditDialogOpen(true)}
                        disabled={order.isHandledByVeloyd}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                    </TooltipTrigger>
                    {order.isHandledByVeloyd && (
                      <TooltipContent>Editing is disabled for orders handled externally.</TooltipContent>
                    )}
                  </Tooltip>
                )} */}

                {/* Cancel Button */}
                {((isAdminOrMerchant && order?.carrierIntegration?.capabilities?.canCancel) ||
                  order.status === 'ADDRESS_NOT_FOUND') && (
                  // Consider using Shadcn AlertDialog for confirmation
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCancelOrder(order.orderId)}
                    className="border-destructive text-destructive hover:bg-destructive/10"
                    disabled={isCancellingOrder}
                  >
                    {isCancellingOrder ? (
                      <>
                        <span className="mr-2 h-4 w-4 animate-spin">⏳</span> Cancelling...
                      </>
                    ) : (
                      <>
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                      </>
                    )}
                  </Button>
                )}

                {/* Actions Popover */}
                <Popover open={isActionsPopoverOpen} onOpenChange={setIsActionsPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="icon">
                      <EllipsisVertical className="h-4 w-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-60" align="end">
                    <div className="grid gap-4">
                      {/* <Button
                        variant="ghost"
                        size="sm"
                        className="justify-start"
                        onClick={() => onClickCopy(`APP_URL/orders/${order.orderId}`)}
                      >
                        <FileText className="mr-2 h-4 w-4" /> Share Order Details
                      </Button> */}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="justify-start"
                            disabled={!tntUrl}
                            onClick={() => {
                              if (tntUrl) {
                                onClickCopy(tntUrl);
                                window.open(tntUrl, '_blank');
                              }
                            }}
                          >
                            <ExternalLink className="mr-2 h-4 w-4" /> Track & Trace
                          </Button>
                        </TooltipTrigger>
                        {!tntUrl && <TooltipContent>No track n trace available (yet).</TooltipContent>}
                      </Tooltip>
                      {isAdmin && !!carrierTntUrl && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="justify-start"
                          onClick={() => {
                            onClickCopy(carrierTntUrl);
                            window.open(carrierTntUrl, '_blank');
                          }}
                        >
                          <Code2 className="mr-2 h-4 w-4" /> Carrier Track & Trace
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="justify-start"
                        onClick={() => {
                          setIsReportIssueDialogOpen(true);
                          setIsActionsPopoverOpen(false);
                        }}
                      >
                        <AlertCircle className="mr-2 h-4 w-4" /> Report Issue
                      </Button>
                      {/* TODO: Conditionally show based on status */}
                      {order.status !== 'ERROR' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="justify-start"
                          onClick={() => handleGetLabel(order.orderId)}
                          disabled={isPrintingLabels}
                        >
                          {isPrintingLabels ? (
                            <>
                              <span className="mr-2 h-4 w-4 animate-spin">⏳</span> Printing...
                            </>
                          ) : (
                            <>
                              <Package className="mr-2 h-4 w-4" /> Print Label
                            </>
                          )}
                        </Button>
                      )}

                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="justify-start"
                          onClick={() => {
                            setIsEditDialogOpen(true);
                            setIsActionsPopoverOpen(false);
                          }}
                        >
                          <Edit className="mr-2 h-4 w-4" /> Edit
                        </Button>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:items-stretch w-full">
              {/* Left Column (Order Info, Bundle, Note) */}
              <div className="lg:col-span-2 space-y-6 flex flex-col">
                {/* Order Info Card */}
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-start gap-2">
                      {/* Left side: Type, ID, Icons */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {order.mailbox && (
                          <Tooltip>
                            <TooltipTrigger>
                              <Mail className="h-5 w-5 text-muted-foreground" />
                            </TooltipTrigger>
                            <TooltipContent>Mailbox Parcel</TooltipContent>
                          </Tooltip>
                        )}
                        <Badge variant="outline" className={getStatusBadgeStyles(order.status)}>
                          {formatOrderStatus(order.status)}
                        </Badge>
                        <div
                          className="flex items-center gap-1 cursor-pointer"
                          onClick={() => onClickCopy(order.orderId)}
                        >
                          <span className="font-mono text-sm font-medium">{order.orderId}</span>
                          <Copy className="h-3 w-3 text-muted-foreground" />
                        </div>
                        {isAdmin && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => {
                                  setShowLogsForOrder(order.id);
                                  setIsTraceLogModalOpen(true);
                                }}
                              >
                                <Code2 className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>View Trace Logs</TooltipContent>
                          </Tooltip>
                          // Add Dialog for Trace Logs here
                        )}
                        {isAdminOrMerchant && (order.notOnInvoice || !order.firstCarrierScanAt) && (
                          <Tooltip>
                            <TooltipTrigger>
                              <Euro className="h-4 w-4 text-muted-foreground" />
                            </TooltipTrigger>
                            <TooltipContent>This order is not on the invoice</TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                      {/* Right side: Created Info */}
                      <div className="text-xs text-muted-foreground text-right whitespace-nowrap">
                        Created {order.createdBy && `from ${order.createdBy}`} at
                        <br />
                        {/* Format date properly later */}
                        <b>{new Date(order.createdAt).toLocaleString()}</b>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Order Ref</p>
                        <p
                          className="font-medium flex items-center gap-1 cursor-pointer truncate"
                          onClick={() => onClickCopy(order.orderRef)}
                        >
                          <span className="truncate">{order.orderRef ? `${order.orderRef}...` : '-'}</span>
                          <Copy className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">
                          {order.deliveryEtaDate ? 'Delivery ETA' : 'Delivery Date'}
                        </p>
                        <p className="font-medium">
                          {order.deliveryEtaDate
                            ? moment(order.deliveryEtaDate).format(DAY_FORMAT)
                            : order.deliveryDate
                              ? moment(order.deliveryDate).format(DAY_FORMAT)
                              : 'n/a'}
                        </p>{' '}
                        {/* Format date */}
                      </div>
                      {/* <div>
                        <p className="text-sm text-muted-foreground">Attempts</p>
                        <p className="font-medium">{order.attempt}</p>
                      </div> */}
                      {/* <div>
                        <p className="text-sm text-muted-foreground">Name</p>
                        <p className="font-medium truncate">{order.name || '-'}</p>
                      </div> */}
                      {/* <div>
                        <p className="text-sm text-muted-foreground">Status</p>
                        <Badge variant="outline">{order.status?.replace('_', ' ') || '-'}</Badge>
                      </div> */}
                      <div>
                        <p className="text-sm text-muted-foreground">Size (LxWxH)</p>
                        <p className="font-medium">{`${order.length || '-'} x ${order.width || '-'} x ${order.height || '-'}`}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Weight</p>
                        <p className="font-medium">{order.weight || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Barcode</p>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <p className="font-medium truncate cursor-default">{order.barcode || '-'}</p>
                          </TooltipTrigger>
                          <TooltipContent className="bg-white border border-black">
                            <div className="flex flex-col items-center">
                              <p className="mb-2">{order.barcode}</p>
                              {order.barcode && (
                                <img
                                  src={`https://bwipjs-api.metafloor.com/?bcid=code128&text=${encodeURIComponent(order.barcode)}&scale=2&includetext=true`}
                                  alt="Barcode"
                                  className="max-w-[200px]"
                                />
                              )}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-grow">
                  {/* Pickup Card */}
                  <Card>
                    <CardHeader>
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex items-center gap-3">
                          {!order.isReturn && order.location?.company?.logoThumbUrl && (
                            <img
                              src={order.location.company.logoThumbUrl}
                              alt={order.location.company.name}
                              className="h-10 w-10 rounded-sm object-contain"
                            />
                          )}
                          <div>
                            <CardTitle className="text-base">
                              {order.isReturn ? order.pickupName : order.location?.name || '-'}
                            </CardTitle>
                            <CardDescription>
                              {!order.isReturn ? order.location?.locationId : order.deliveryEmail || '-'}
                            </CardDescription>
                          </div>
                          {order.isReturn && <Badge variant="outline">Return</Badge>}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                          <MapPin className="h-3 w-3" /> Address
                        </p>
                        <p
                          className="text-sm font-medium flex items-center gap-1 cursor-pointer"
                          onClick={() => onClickCopy(order.pickupAddress)}
                        >
                          {order.pickupAddress || '-'} <Copy className="h-3 w-3 text-muted-foreground" />
                        </p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                            <Phone className="h-3 w-3" /> Phone
                          </p>
                          <p className="text-sm font-medium">{order.pickupPhone || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                            <StickyNote className="h-3 w-3" /> Note
                          </p>
                          <p className="text-sm font-medium">{order.pickupNote || '-'}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Delivery Card */}
                  <Card>
                    <CardHeader>
                      <div className="flex justify-between items-start gap-2">
                        <div>
                          <CardTitle className="text-base">{order.deliveryName || '-'}</CardTitle>
                          <CardDescription>{order.deliveryEmail || '-'}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                          <MapPin className="h-3 w-3" /> Address
                        </p>
                        <p
                          className="text-sm font-medium flex items-center gap-1 cursor-pointer"
                          onClick={() => onClickCopy(order.deliveryAddress)}
                        >
                          {order.deliveryAddress || '-'} <Copy className="h-3 w-3 text-muted-foreground" />
                        </p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                            <Phone className="h-3 w-3" /> Phone
                          </p>
                          <p className="text-sm font-medium">{order.deliveryPhone || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                            <StickyNote className="h-3 w-3" /> Note
                          </p>
                          <p className="text-sm font-medium">{order.deliveryNote || '-'}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Right Column (Timeline, Review) */}
              <div className="lg:col-span-1 space-y-6 flex flex-col">
                {/* Timeline Card */}
                <Card className="h-full flex flex-col">
                  <CardHeader>
                    <CardTitle className="text-base">Timeline</CardTitle>
                  </CardHeader>
                  <CardContent className="flex-grow overflow-y-auto max-h-[440px]">
                    <OrderTimeLine
                      order={order}
                      tntSentEvents={order?.messagingLogs.filter((log) => log.lockedUntil !== null)}
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="overflow-hidden h-[400px]">
                {!user.fullyTracked && (
                  <CardHeader>
                    <div className="flex justify-between items-center gap-2">
                      <CardTitle className="text-base">Map and route visualization</CardTitle>
                    </div>
                  </CardHeader>
                )}
                <CardContent className="p-0">
                  {/* Map component with fixed height to ensure visibility */}
                  <div className="h-[400px] w-full">
                    {!user.fullyTracked ? (
                      <div className="ml-6">
                        <em>Not available for your service level.</em>
                      </div>
                    ) : (
                      <DefaultMapbox markers={markers} className="h-full w-full" />
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Delivery Status / POD / Signature Card */}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center gap-2">
                    <CardTitle className="text-base">Delivery Status & Proof</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!user.fullyTracked ? (
                    <div>
                      <em>Not available for your service level.</em>
                    </div>
                  ) : (
                    <>
                      {/* Status Details */}
                      <div className="flex items-center justify-start gap-2">
                        <p className="text-sm text-muted-foreground">Attempts</p>
                        <Badge variant="secondary">{order.attempt}</Badge>
                      </div>
                      <div>
                        {order.packageWas && (
                          <p className="text-sm">
                            Order was: <b>{order.packageWas.replace('_', ' ')}</b>
                          </p>
                        )}
                        {order.neighbourAddress && (
                          <p className="text-sm">
                            Neighbour address: <b>{order.neighbourAddress}</b>
                          </p>
                        )}
                        {order.status === 'AWAITING_NEXT_ATTEMPT' && order.failedReason && (
                          <p className="text-sm">
                            Failed reason: <b>{order.failedReason}</b>
                          </p>
                        )}
                        {/* Show message if no details? */}
                        {!order.packageWas &&
                          !order.neighbourAddress &&
                          !(order.status === 'AWAITING_NEXT_ATTEMPT' && order.failedReason) && (
                            <p className="text-sm text-muted-foreground">No delivery status details recorded yet.</p>
                          )}
                      </div>

                      <Separator />

                      {/* POD & Signature Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* POD Section */}
                        <div>
                          <p className="text-sm font-medium mb-2 flex items-center gap-1">
                            <ImageIcon className="h-4 w-4" /> POD Images
                          </p>

                          {order.pods && order.pods.length > 0 ? (
                            <ImageGallery pods={order.pods} />
                          ) : (
                            <p className="text-sm text-muted-foreground mt-2">
                              No POD images available.
                            </p> /* Added mt-2 for consistency */
                          )}
                        </div>
                        {/* Signature Section */}
                        <div className="border-t md:border-t-0 md:border-l pt-4 md:pt-0 md:pl-4">
                          <p className="text-sm font-medium mb-2 flex items-center gap-1">
                            <Signature className="h-4 w-4" /> Signature
                          </p>
                          {order.signatureImage ? (
                            <AspectRatio ratio={1 / 2} className="bg-gray-100 rounded overflow-hidden">
                              <SignatureDisplay signature={order?.signatureImage || undefined} />
                            </AspectRatio>
                          ) : (
                            <p className="text-sm text-muted-foreground">No signature captured.</p>
                          )}
                        </div>
                      </div>

                      <Separator />

                      {/* Rider Note */}
                      <div>
                        <p className="text-sm font-medium mb-2 flex items-center gap-1">
                          <MessageSquare className="h-4 w-4" /> Rider Note
                        </p>
                        <p className="text-sm bg-muted p-3 rounded">{order.riderNote || '-'}</p>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Dialogs for Editing, Reporting Issues, etc. */}
            <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
              <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                  <DialogTitle>Edit Order Info</DialogTitle>
                  <DialogDescription>
                    Make changes to the order details. Click save when you are done.
                  </DialogDescription>
                </DialogHeader>

                <EditOrderForm params={editFormParams} onChange={setEditFormParams} />
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      handleUpdateOrder(order.orderId, editFormParams);
                      setIsEditDialogOpen(false);
                    }}
                  >
                    Save changes
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={isReportIssueDialogOpen} onOpenChange={setIsReportIssueDialogOpen}>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>Report an Issue</DialogTitle>
                  <DialogDescription>Describe the issue with this order.</DialogDescription>
                </DialogHeader>
                <NewReportIssueForm
                  issue={{
                    orderId: order?.id || 0,
                    carrierName: order?.carrierIntegration?.carrierName || '',
                    companyName: order?.location?.company?.name || '',
                  }}
                  userId={user?.id}
                  onCancel={() => setIsReportIssueDialogOpen(false)}
                  onSuccess={() => {
                    setIsReportIssueModalOpen(false);
                  }}
                />
              </DialogContent>
            </Dialog>

            <Dialog open={isIssueModalOpen} onOpenChange={setIsIssueModalOpen}>
              <DialogContent className="sm:max-w-[750px]">
                <DialogHeader>
                  <DialogTitle>Issue</DialogTitle>
                </DialogHeader>
                {isIssueLoaded && <IssueChat selectedIssue={issue} userId={user?.id} />}
              </DialogContent>
            </Dialog>

            <Dialog
              open={isTraceLogModalOpen}
              onOpenChange={() => {
                setIsTraceLogModalOpen(false);
                setShowLogsForOrder(null);
              }}
            >
              <DialogContent className="sm:max-w-[750px]">
                <DialogHeader>
                  <DialogTitle>Trace logs</DialogTitle>
                </DialogHeader>
                <TraceLogs show={!!showLogsForOrder} preset={{ subject: 'ORDER', subjectId: showLogsForOrder }} />
              </DialogContent>
            </Dialog>
          </div>
        </TooltipProvider>
      </div>
    </div>
  );
};

export default withPage(OrderDetailPage);

const EditOrderForm = ({ params, onChange }: { params: any; onChange: (params: any) => void }) => {
  return (
    <div className="py-4">
      <Select
        value={params.status}
        onValueChange={(value) => onChange({ ...params, status: value.replace(' ', '_') })} // 4. Update status in parent state
      >
        <SelectTrigger>
          <SelectValue placeholder="Update Order Status" />
        </SelectTrigger>
        <SelectContent position="popper" side="bottom" align="start" className="max-h-[200px] overflow-y-auto">
          {Object.values(ORDER_STATUSES).map((status) => (
            <SelectItem key={status} value={status.replace(' ', '_')}>
              {status}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

const ImageGallery = ({ pods }: { pods?: string[] }) => {
  if (pods && pods.length > 0) {
    const images = pods.map((pod) => `${process.env.REACT_APP_S3_URL}${pod}`);

    return (
      // Use Tailwind grid classes for layout
      <div className="grid grid-cols-2 gap-2 mt-2">
        {images.map((src, index) => (
          // Add aspect ratio and object cover for consistent image display
          <AspectRatio key={index} ratio={1 / 1} className="bg-muted rounded-md overflow-hidden">
            <img
              src={src}
              key={index}
              alt={`POD ${index + 1}`}
              // Use Tailwind classes for styling and add object-cover
              className="w-full h-full object-cover block cursor-pointer"
              onClick={() => {
                // Keep the preview functionality
                window.open(src, '_blank');
              }}
            />
          </AspectRatio>
        ))}
      </div>
    );
  } else {
    // Return null or placeholder if no pods
    return <p className="text-sm text-muted-foreground mt-2">No POD images available.</p>;
  }
};
